/**
 * AvizorListener — escucha global de avizores (videoperitación inmediata).
 *
 * Polling cada 5s a `/avizor/pending`. Si recibe uno o más avizores
 * dirigidos al usuario, muestra un modal bloqueante con beep insistente
 * (Web Audio API, sonando cada 3s) y dos acciones: Aceptar / Rechazar.
 *
 * Al aceptar:
 *   - Llama `POST /avizor/{id}/accept` → backend crea sesión Ekspertium
 *     Video y persiste el subencargo.
 *   - Navega al expediente con `?tab=videoperitaje` y abre la sala en
 *     una pestaña nueva (`video_expert_url`).
 *
 * Al rechazar:
 *   - Llama `POST /avizor/{id}/reject` con razón opcional.
 *
 * El polling solo se activa para roles perito_interno / perito_externo.
 *
 * La UI del modal vive en `AvizorIncomingModal.jsx` y el beep en
 * `lib/avizorBeep.js`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  showChatNotification,
  ensureNotificationPermission,
} from "@/lib/chatNotifications";
import { playAvizorBeep } from "@/lib/avizorBeep";
import AvizorIncomingModal from "@/components/AvizorIncomingModal";
import { ROLES_PERITO_LIKE } from "@/lib/roles";
import { toast } from "sonner";

const POLL_MS = 5000;
const BEEP_MS = 3000;
const DEFAULT_TIMEOUT_SECONDS = 60;
// Un admin con `code` configurado también puede recibir Avizores
// (el admin puede actuar como perito).
const PERITO_ROLES = ROLES_PERITO_LIKE;

/** Cuenta atrás hasta `expires_at` (segundos), o el default si no viene. */
function computeSecondsLeft(av) {
  if (av?.expires_at) {
    const ms = new Date(av.expires_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 1000));
  }
  return DEFAULT_TIMEOUT_SECONDS;
}

/** Lanza la notificación nativa del SO si la pestaña no tiene foco. */
function notifyAvizor(av) {
  const resumen = av.expediente_resumen || {};
  const sinies = resumen.numero_siniestro ? ` · ${resumen.numero_siniestro}` : "";
  showChatNotification({
    title: `Avizor entrante · ${av.tipo === "reparador" ? "reparador" : "asegurado"}`,
    body: `${av.gestor_name || "Un gestor"}${sinies}${
      av.message ? ` — ${av.message}` : ""
    }`,
    tag: `avizor-${av.id}`,
    onClick: () => {
      try { window.focus(); } catch { /* ignore */ }
    },
  });
}

export default function AvizorListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [acting, setActing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const dismissedRef = useRef(new Set()); // ids ya rechazados/expirados localmente

  const isPerito = !!user && PERITO_ROLES.has(user.role);

  // Pedir permiso de notificación una sola vez al montar (sólo peritos).
  useEffect(() => {
    if (isPerito) ensureNotificationPermission().catch(() => {});
  }, [isPerito]);

  // -------- Poll periódico
  useEffect(() => {
    if (!isPerito) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/avizor/pending");
        if (cancelled) return;
        const items = (data?.items || []).filter(
          (it) => !dismissedRef.current.has(it.id),
        );
        if (items.length && !current) {
          const av = items[0]; // sort desc por created_at desde el backend
          setCurrent(av);
          setSecondsLeft(computeSecondsLeft(av));
          playAvizorBeep();
          notifyAvizor(av);
        }
      } catch {
        /* 401/network: silenciar; el AuthContext gestiona logout */
      }
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isPerito, current]);

  // -------- Beep en bucle + countdown mientras hay un avizor activo
  useEffect(() => {
    if (!current) return undefined;
    const beepId = setInterval(playAvizorBeep, BEEP_MS);
    const tickId = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          // Timeout: cerramos automáticamente
          dismissedRef.current.add(current.id);
          setCurrent(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      clearInterval(beepId);
      clearInterval(tickId);
    };
  }, [current]);

  // -------- Acciones
  const onAccept = useCallback(async () => {
    if (!current || acting) return;
    setActing(true);
    try {
      const { data } = await api.post(`/avizor/${current.id}/accept`);
      toast.success("Videoperitación iniciada");
      if (data?.video_expert_url) {
        try {
          window.open(data.video_expert_url, "_blank", "noopener,noreferrer");
        } catch {
          /* bloqueado por el navegador; se abrirá desde la pestaña Videoperitaje */
        }
      }
      const expId = current.expediente_id;
      setCurrent(null);
      if (expId) navigate(`/review/${expId}?tab=videoperitaje`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo aceptar el avizor";
      toast.error(msg);
      if (e?.response?.status === 409) {
        dismissedRef.current.add(current.id);
        setCurrent(null);
      }
    } finally {
      setActing(false);
    }
  }, [current, acting, navigate]);

  const onReject = useCallback(async () => {
    if (!current || acting) return;
    setActing(true);
    try {
      await api.post(`/avizor/${current.id}/reject`, { reason: "" });
      toast.info("Avizor rechazado");
    } catch {
      /* noop */
    } finally {
      dismissedRef.current.add(current.id);
      setCurrent(null);
      setActing(false);
    }
  }, [current, acting]);

  return (
    <AvizorIncomingModal
      avizor={current}
      secondsLeft={secondsLeft}
      busy={acting}
      onAccept={onAccept}
      onReject={onReject}
    />
  );
}
